// Owned by mju-ai-workbench/shared/schemas/companion.ts — the Mojo Up companion app's contract with the workbench (D343). The AIOE session mirrors it to mju-aioe/packages/shared/src/schemas/companion.ts; the workbench never mirrors it back. Keep the two identical.
/**
 * The Mojo Up companion app (iOS and Android, `mju-aioe/packages/mobile`) and what the workbench answers it
 * (D343, `docs/plans/2026-10-companion-app.md`). OURS: the workbench defines these shapes, the app and the AIOE
 * relay mirror them. Everything here is zod + types + constants; no workbench import, so a mirror is a file copy.
 *
 * Methods (Control API unless said; scope in brackets; `rootId` names a repository as `workspaces.list` does, and is
 * the same as the `workspace` param every method already takes):
 *
 *   companion.overview [agents:read]        {} -> {@link CompanionOverview}: every repository on this workbench (or hub), its
 *                                            members in the Inbox's words, and what waits on the person.
 *   companion.home [home:read]              {@link CompanionHomeRequest} -> {@link CompanionHome}: Home (the day strip, the stats, the feed)
 *                                            as the desktop draws it, built on the host from the same models (shared/home/).
 *   companion.home.feedback [home:read]     {@link CompanionHomeFeedback} -> {@link CompanionHomeFeedbackAnswer}: useful / not useful / pin / hide /
 *                                            done / show one card, or {@link CompanionHomeBatchAnswer} for clear (the whole feed) and
 *                                            a batch show; kept per machine so the desktop and the phone agree.
 *   apps.list [agents:read]                 {} -> { apps: [{ id, name, purpose, icon, tone, version, custom?, blocked? }] }: the person's apps.
 *   apps.get [agents:read]                  { appId } -> { ok, manifest?, refused? }: the app's screen (UiSpecV1, or a custom view), reads and actions.
 *   apps.read [agents:read + the read's scope]   { appId, read, params? } -> { ok, value?, message? }: one declared read, through the same runtime as
 *                                            the desktop (scope ceiling, DLP floor, masked, capped, audited as microapp.read). params are small scalars.
 *   apps.act [agents:read + the action's scope]  { appId, action, input?: { values?, row?, confirmed? } } -> { id, name, ok, message, needsConfirm? }: a declared
 *                                            action as the caller, audited with the app's id and version. An action with a confirm sentence answers needsConfirm
 *                                            first; call again with confirmed: true. The phone preset has no write scope, so an app action that writes is
 *                                            refused there unless the person paired with more. See shared/apps/phoneControls.ts.
 *   terminals.list [terminals:read]         {} -> {@link TerminalList}.
 *   terminals.remote [agents:write, at the workbench only]   { terminalId, on } or { all: true, on: false } -> the state.
 *   terminals.subscribe  CHANNEL method, `input: true` when the phone types [terminals:read, + terminals:write for input]
 *                                            open params {@link TerminalSubscribeParams}; `opened.result`
 *                                            {@link TerminalOpened}; `data` = base64 of the raw PTY bytes; `input` payloads
 *                                            {@link TerminalInput}; channel `close` = unsubscribe (there is no `terminals.unsubscribe`
 *                                            or `terminals.resize` RPC: resize is an `input` frame).
 *   sessions.follow      CHANNEL method, `input: false` [agents:read]   open params {@link SessionFollowParams};
 *                                            `opened.result` {@link SessionFollowOpened}; `data` = one LiveEvent object
 *                                            (shared/schemas/remoteControl.ts) as JSON, not base64.
 *   git.diff [agents:read]                  {@link GitDiffRequest} -> {@link GitDiffAnswer}, bounded.
 *   review.notes.send [prompts:write]       {@link ReviewNotesSend} -> {@link ReviewNotesSent}: one batch to the agent as one prompt.
 *   companion.pair.begin [the workbench's own person only]   {@link CompanionPairBegin} -> {@link CompanionPairingBegun}.
 *   POST {endpoint}/companion/v1/pair (public, workbench pairing) or POST {hub}/relay/device/join (public, hub pairing):
 *                                            {@link CompanionTokenRequest} -> {@link CompanionToken}.
 *
 * Refusals are JSON-RPC errors (code -32003 for a sentence for a person, -32001 for a missing scope) whose
 * `data.error` is one of {@link COMPANION_ERRORS}; on a channel `open` they are `closed { code, reason }` with the
 * reason `"<error>: <message>"` (`refused` for remote_control_off and the rest, `scope` for terminal_scope).
 *
 * Words for people: repository, member, needs you, thinking, calling tools, needs approval, idle, terminal, review note.
 */
import { z } from "zod";
import { homeBundleSchema, homeFeedKindSchema } from "./home.js";
export const COMPANION_SCHEMA_VERSION = 1;
// ── The phone scope preset ─────────────────────────────────────────────
/**
 * What a paired phone may do, as Control API scopes (src/control/workbenchControl.ts `ControlScope`):
 *
 *  - read agents, sessions, approvals, the Inbox's questions and the board: `agents:read`, `tasks:read`
 *  - send a prompt or a steer to a session: `prompts:write`
 *  - approve or decline a tool call: `permissions:write` (levels 4-5 still need a project owner, D270)
 *  - stop a session: `agents:write` (coarse: it also covers `sessions.reset`, `sessions.restart` and `sessions.handOff`; there is no narrower scope)
 *  - read Home and teach its feed: `home:read` (the day strip, the stats row and the cards; `companion.home.feedback` only
 *    changes per-machine preferences about which cards show first)
 *
 * Deliberately not in it: `settings:write`, `git:write`, `tasks:write`, `composer:write`, `memory:write`, `teams:write`,
 * `ui:view`, `ui:control`, `diagrams:*`. A phone cannot write a file, change a setting, land a branch or edit the board.
 */
export const COMPANION_SCOPES = ["agents:read", "tasks:read", "prompts:write", "permissions:write", "agents:write", "home:read"];
/**
 * Added only when the person ticks "Let the phone use my terminals" while pairing. A terminal is a shell: typing in it
 * can write files, so this is never in the default preset, and each terminal must also be opened to remote control.
 */
export const COMPANION_TERMINAL_SCOPES = ["terminals:read", "terminals:write"];
/** What the phone preset leaves out, for the pairing screen to say. */
export const COMPANION_EXCLUDED_SCOPES = ["settings:write", "git:write", "tasks:write", "composer:write", "memory:write", "teams:write", "ui:view", "ui:control", "diagrams:read", "diagrams:write"];
/** Each scope in the words the pairing screen shows. */
export const COMPANION_SCOPE_WORDS = {
    "agents:read": "See your agents and sessions",
    "tasks:read": "See the board and what waits on you",
    "prompts:write": "Send a message to an agent",
    "permissions:write": "Approve or decline what an agent asks",
    "agents:write": "Stop an agent",
    "home:read": "See Home: this week, the numbers and the feed",
    "terminals:read": "Watch the terminals you open to the phone",
    "terminals:write": "Type in the terminals you open to the phone",
};
export const COMPANION_PLATFORMS = ["ios", "android"];
export const companionPlatformSchema = z.enum(COMPANION_PLATFORMS);
/** What the phone sends to redeem a pairing code. */
export const companionTokenRequestSchema = z.object({
    /** The pairing's one-time code: typed in any form ({@link normalisePairingCode}) or read from the QR. */
    code: z.string().min(1).max(40),
    /** What the person calls this phone; shown in the workbench's paired devices and the audit. */
    deviceName: z.string().min(1).max(120),
    platform: companionPlatformSchema,
    appVersion: z.string().min(1).max(40),
    /** Reserved: a directly paired workbench has no push service and ignores it; organisation phones get push through the organisation. */
    pushToken: z.string().min(1).max(500).optional(),
});
/** The answer to a redeem on a workbench (`kind: "workbench"`). A hub answers with its own join answer (`scope: "hub"`). */
export const companionTokenSchema = z.object({
    access_token: z.string().min(1),
    /** The paired device's id on this workbench: what Settings and the audit call it, and what revokes it. */
    client_id: z.string().min(1),
    scopes: z.array(z.string()),
    /** What the person calls this workbench. */
    name: z.string().max(200),
    /** The Control API base the token works at (the pairing's endpoint). */
    endpoint: z.string().url(),
    /** Days of not being used after which the token stops working; 0 = never. */
    idleDays: z.number().int().nonnegative().optional(),
});
// ── Overview ───────────────────────────────────────────────────────────
/** A member's status, in the Inbox and Agents view's words (D335: `agentStatus()`); the words are `statusWords`. */
export const COMPANION_STATUSES = ["starting", "thinking", "calling-tools", "needs-approval", "with-person", "idle", "error"];
export const companionStatusSchema = z.enum(COMPANION_STATUSES);
const STATUS_OF = {
    starting: ["starting", "Starting"],
    thinking: ["thinking", "Thinking"],
    tool_calling: ["calling-tools", "Calling tools"],
    waiting_approval: ["needs-approval", "Needs approval"],
    with_person: ["with-person", "With a person in a terminal"],
    idle: ["idle", "Idle"],
    ready: ["idle", "Ready"],
    stopped: ["idle", "Stopped"],
    error: ["error", "Error"],
};
/** A runtime's raw status (src/acp/agentManager.ts) in the companion's status and the words the desktop shows for it. */
export function companionStatusOf(raw) {
    const hit = STATUS_OF[raw];
    if (hit)
        return { status: hit[0], statusWords: hit[1] };
    return { status: "idle", statusWords: raw.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) };
}
/** What a person is being waited on for. */
export const COMPANION_WAIT_KINDS = ["approval", "question", "review"];
export const companionWaitSchema = z.object({
    kind: z.enum(COMPANION_WAIT_KINDS),
    /** ISO time it started waiting. */
    since: z.string().max(40),
    /** The permission id (`permissions.resolve`), the decision id (`decisions.answer`) or the task id (a task in review). */
    id: z.string().min(1).max(200),
    /** One line of what it is, with D270's risk words for an approval. */
    title: z.string().max(300).optional(),
    riskLevel: z.number().int().min(1).max(5).optional(),
});
export const companionMemberSchema = z.object({
    /** The team's id (`solo` for a specialist or a chat). */
    teamId: z.string().min(1).max(200),
    /** The member's name as its team knows it. */
    name: z.string().min(1).max(200),
    sessionId: z.string().min(1).max(200),
    status: companionStatusSchema,
    statusWords: z.string().max(100),
    /** What it is working on, in a line. */
    task: z.string().max(300).optional(),
    /** The board task it is working on (`git.diff { rootId, task: taskId, teamId }`); with `task`, when it has one. */
    taskId: z.string().min(1).max(200).optional(),
    /** The commit of the newest turn checkpoint its session ended (D125): `git.diff { rootId, checkpoint }`. Absent until a turn has ended since the host started. */
    checkpoint: z.string().regex(/^[0-9a-f]{7,64}$/).optional(),
    waitingOn: companionWaitSchema.optional(),
});
export const companionRootSchema = z.object({
    rootId: z.string().min(1).max(200),
    /** The repository's name. */
    name: z.string().min(1).max(200),
    /** Where it runs, in words: "This computer", "WSL: Ubuntu", "a dev container", or the node's name. */
    place: z.string().max(200),
    /** The machine's name. */
    machine: z.string().max(200).optional(),
    /** Its workbench is not running or did not answer: no members are listed, and this says why. */
    unavailable: z.string().max(300).optional(),
    members: z.array(companionMemberSchema).max(200),
});
/** Everything that waits on the person, oldest first, across every repository: the phone's home. */
export const companionNeedsYouSchema = companionWaitSchema.extend({
    rootId: z.string().min(1).max(200),
    rootName: z.string().max(200),
    teamId: z.string().max(200).optional(),
    sessionId: z.string().max(200).optional(),
    /** The member that waits on it, by name. */
    member: z.string().max(200).optional(),
});
export const companionOverviewSchema = z.object({
    v: z.literal(COMPANION_SCHEMA_VERSION),
    /** ISO time. */
    at: z.string().max(40),
    /** What this workbench is called. */
    name: z.string().max(200),
    roots: z.array(companionRootSchema).max(200),
    needsYou: z.array(companionNeedsYouSchema).max(500),
});
// ── Home ───────────────────────────────────────────────────────────────
/**
 * What the feedback buttons on a card do. `pin` toggles; `useful` and `not-useful` lean the card's whole kind (pressing the one
 * already on clears it); `hide` hides the card for a week unless its record changes.
 *
 * Additive (0.89.19), the desktop's Clear, Clear all and their way back:
 *  - `done` ("Clear" in words) puts one card away until what it says changes (no week). Only a card that does not ask
 *    something: one with `asks: true` on its wire card is refused with `card_asks` — it is answered, not cleared.
 *  - `clear` ("Clear all") takes no `cardId`: every card of the whole feed on the host — not only the cards the phone was
 *    sent — is put away as done, except pinned cards and cards that ask. Answered with the ids it put away
 *    ({@link CompanionHomeBatchAnswer}: at most {@link COMPANION_PUT_AWAY_MAX}, `more` counts the rest); `show` with those ids
 *    is the batch's one Undo.
 *  - `show` brings back a card that was put away (done or hidden): one by `cardId`, or a whole batch by `cardIds` (the Undo
 *    of a `clear`). A card that is not put away is `card_not_found`.
 * What is put away right now is listed in `companion.home`'s `putAway`, so the phone can offer each card's own way back.
 */
export const COMPANION_HOME_FEEDBACK_ACTIONS = ["useful", "not-useful", "pin", "hide", "done", "clear", "show"];
/** The most put-away cards `companion.home` lists, and the most ids a `clear` answers with. */
export const COMPANION_PUT_AWAY_MAX = 200;
/** The most cards one batch `show` names: a whole `clear`'s answer. */
export const COMPANION_SHOW_MAX = COMPANION_PUT_AWAY_MAX;
/** The most quick runs and other projects `companion.home` lists. */
export const COMPANION_QUICK_RUNS_MAX = 20;
export const COMPANION_OTHER_PROJECTS_MAX = 50;
/**
 * Additive (0.89.19): one of the person's quick runs for this project (Home's Run now row, in the person's order). The phone
 * lists them; there is no Control API method that runs a routine (the desktop runs it through its own `routines.runNow`), so
 * a phone shows the list and where to run it, nothing more.
 */
export const companionQuickRunSchema = z.object({
    id: z.string().max(200),
    /** The routine's name. */
    label: z.string().max(200),
    /** What it runs, in a line: "Daily review — read the day, write prescriptions". */
    runs: z.string().max(200),
    /** How its last run ended, in words and as a state (never colour alone): "Done 2 hours ago", "Not run yet". */
    last: z.object({ state: z.enum(["ok", "error", "skipped", "never"]), label: z.string().max(80), at: z.string().max(40).optional() }),
    /** It is turned off in Routines. */
    paused: z.literal(true).optional(),
});
/** Additive (0.89.19): another of the person's projects, as Home's "Your other projects" line shows it. */
export const companionOtherProjectSchema = z.object({
    id: z.string().max(200),
    name: z.string().max(200),
    home: z.enum(["private", "hub", "organisation"]),
    /** "2 need you", "nothing waiting". */
    status: z.string().max(120),
    /** Needs-you records waiting in it (the count the status says). */
    needsYou: z.number().int().min(0),
});
/** `companion.home`'s params: the project to show; absent, the one the desktop has open. */
export const companionHomeRequestSchema = z.object({ projectId: z.string().min(1).max(200).optional() });
/**
 * Home for a phone or tablet: `{ v: 1, strip, stats, feed }` exactly as `shared/schemas/home.ts` (`homeBundleSchema`), plus
 * what the host adds about it. Everything else is additive, so a client that reads only the bundle still works.
 */
export const companionHomeSchema = homeBundleSchema.extend({
    /** ISO time the host built it. */
    at: z.string().max(40),
    /** Which project this is Home of, and where the project's home is (`private`, `hub`, `organisation`). */
    project: z.object({ id: z.string().max(200), name: z.string().max(200), home: z.enum(["private", "hub", "organisation"]) }).optional(),
    /** Per card id, what the person taught the feed (the same per-machine preferences the desktop uses). */
    cards: z.record(z.string().max(300), z.object({ pinned: z.boolean().optional(), rating: z.enum(["useful", "not-useful"]).optional() })).optional(),
    /**
     * Additive (0.89.19): the cards put away right now, newest first — by Clear or Clear all (`done`, gone until what they say
     * changes) or hidden for a week (`hidden`) — so each has its own way back (`companion.home.feedback` `show`). Absent when
     * nothing is put away.
     */
    putAway: z
        .array(z.object({ id: z.string().max(300), kind: homeFeedKindSchema, kicker: z.string().max(120), title: z.string().max(300), state: z.enum(["done", "hidden"]) }))
        .max(COMPANION_PUT_AWAY_MAX)
        .optional(),
    /** Additive (0.89.19): the person's quick runs for this project, in their order. Absent when the host could not read them. */
    quickRuns: z.array(companionQuickRunSchema).max(COMPANION_QUICK_RUNS_MAX).optional(),
    /** Additive (0.89.19): the person's other projects, with what waits in each. Absent when there are none. */
    otherProjects: z.array(companionOtherProjectSchema).max(COMPANION_OTHER_PROJECTS_MAX).optional(),
    /**
     * What a host cannot compute and leaves out rather than guess: the names of the inputs missing here, in words
     * ("Pull requests", "Plan usage", "What the team is working on now", "Tasks ready to send", "While you were away").
     * The matching cards and the Plan usage number are simply absent; no second model fills in for them.
     */
    notComputed: z.array(z.string().max(120)).max(12),
});
export const companionHomeFeedbackSchema = z
    .object({
    /** A feed card's `id` (`shared/schemas/home.ts`: `kind:record`). Every action but `clear` (and a batch `show`) names one. */
    cardId: z.string().min(1).max(300).optional(),
    /** `show` only: the cards of one batch (the ids a `clear` answered), brought back at once. */
    cardIds: z.array(z.string().min(1).max(300)).min(1).max(COMPANION_SHOW_MAX).optional(),
    action: z.enum(COMPANION_HOME_FEEDBACK_ACTIONS),
    /** The project the card is on; absent, the one the desktop has open (as `companion.home`). */
    projectId: z.string().min(1).max(200).optional(),
})
    .refine((f) => (f.action === "clear" ? true : f.action === "show" ? !!f.cardId || !!f.cardIds : !!f.cardId), { message: "Name the card: every action but clear takes cardId (show takes cardId or cardIds)." });
// ── Terminals ──────────────────────────────────────────────────────────
/** Refusals, as `data.error` of a JSON-RPC error or the head of a channel `closed.reason`. */
export const COMPANION_ERRORS = {
    /** Remote control is off for this terminal, or the kill switch is on (D282's rule applied to a terminal). */
    remoteControlOff: "remote_control_off",
    /** The token does not carry `terminals:read` (watch) or `terminals:write` (type). */
    terminalScope: "terminal_scope",
    terminalNotFound: "terminal_not_found",
    /** This computer has no terminals to serve (a node, a host with no desktop). */
    terminalUnavailable: "terminal_unavailable",
    /** The method does not take input, or the channel was not opened with `input: true`. */
    inputNotAllowed: "input_not_allowed",
    diffUnavailable: "diff_unavailable",
    /** `companion.home.feedback` named a card the feed does not have right now (it was resolved, or hidden already). */
    cardNotFound: "card_not_found",
    /** `done` named a card that asks something (an approval, a question, a plan waiting): it is answered, not put away. Additive, 0.89.19. */
    cardAsks: "card_asks",
    /** The host could not build Home (no project is open, or its sources did not answer). */
    homeUnavailable: "home_unavailable",
    tooManyNotes: "too_many_notes",
    pairingInvalid: "pairing_invalid",
    pairingExpired: "pairing_expired",
    pairingOff: "pairing_off",
    /** The endpoint a workbench pairing would advertise serves a certificate a phone cannot trust (its own, not the tailnet's). */
    untrustedCertificate: "untrusted_certificate",
};
export const companionRefusalSchema = z.object({ error: z.string(), message: z.string().max(400) });
/** Most raw PTY bytes in one `data` frame (base64 makes it a third larger, inside the channel's 64 KB frame). */
export const TERMINAL_DATA_CHUNK_BYTES = 32 * 1024;
/**
 * Most raw scrollback bytes in `opened.result`: 192 000, which is 256 000 characters of base64. AIOE's device socket takes
 * an `opened` frame up to 512 KB (every other frame, `data` above all, is held to 64 KB); the terminal's own buffer is 256 KB,
 * so `truncatedBytes` counts what of it did not fit.
 */
export const TERMINAL_SCROLLBACK_RESULT_BYTES = 192_000;
export const TERMINAL_MAX_COLS = 500;
export const TERMINAL_MAX_ROWS = 200;
export const terminalInfoSchema = z.object({
    terminalId: z.string().min(1).max(200),
    title: z.string().max(200),
    rootId: z.string().max(200).optional(),
    state: z.enum(["running", "exited"]),
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
    /** Open to the phone: the person turned remote control on for it. */
    remote: z.boolean(),
});
export const terminalListSchema = z.object({ terminals: z.array(terminalInfoSchema).max(100) });
/** `terminals.subscribe`'s open params. `cols` and `rows` open the terminal at the phone's size (the shared PTY is resized). */
export const terminalSubscribeParamsSchema = z.object({
    terminalId: z.string().min(1).max(200),
    cols: z.number().int().min(2).max(TERMINAL_MAX_COLS).optional(),
    rows: z.number().int().min(2).max(TERMINAL_MAX_ROWS).optional(),
    /** Less scrollback than the default, never more than {@link TERMINAL_SCROLLBACK_RESULT_BYTES}. */
    scrollbackBytes: z.number().int().min(0).max(TERMINAL_SCROLLBACK_RESULT_BYTES).optional(),
});
export const terminalOpenedSchema = z.object({
    terminalId: z.string(),
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
    /** base64 of the newest scrollback bytes, at most {@link TERMINAL_SCROLLBACK_RESULT_BYTES} raw. */
    scrollback: z.string(),
    /** Older bytes the terminal still holds that did not fit. */
    truncatedBytes: z.number().int().positive().optional(),
});
/** An `input` frame's payload on a terminal channel. */
export const terminalInputSchema = z.discriminatedUnion("kind", [
    /** What the phone's keyboard sends, base64 of the bytes (at most 16 KB raw a frame). */
    z.object({ kind: z.literal("keys"), data: z.string().max(22_000) }),
    z.object({ kind: z.literal("resize"), cols: z.number().int().min(2).max(TERMINAL_MAX_COLS), rows: z.number().int().min(2).max(TERMINAL_MAX_ROWS) }),
]);
// ── Following a session ────────────────────────────────────────────────
/** `sessions.follow`'s open params: a session or a conversation, as `sessions.subscribe` names it. */
export const sessionFollowParamsSchema = z
    .object({ sessionId: z.string().min(1).max(200).optional(), conversationId: z.string().min(1).max(200).optional(), turns: z.number().int().min(0).max(100).optional() })
    .refine((v) => !!v.sessionId !== !!v.conversationId, { message: "name a sessionId or a conversationId, not both" });
// ── Reading a diff ─────────────────────────────────────────────────────
export const GIT_DIFF_DEFAULT_MAX_BYTES = 256 * 1024;
export const GIT_DIFF_MAX_BYTES = 2 * 1024 * 1024;
export const GIT_DIFF_DEFAULT_MAX_FILES = 50;
export const GIT_DIFF_MAX_FILES = 200;
export const gitDiffRequestSchema = z
    .object({
    rootId: z.string().min(1).max(200),
    /** A task's work: its worktree against where it started. */
    task: z.string().min(1).max(200).optional(),
    /** The board the task is on, when the id alone is not enough. */
    teamId: z.string().min(1).max(200).optional(),
    /** A turn's checkpoint commit (D125): what that turn changed. */
    checkpoint: z.string().regex(/^[0-9a-f]{7,64}$/).optional(),
    /** One file only. */
    path: z.string().min(1).max(1000).optional(),
    /**
     * The conversation the checkpoint is a turn of (D414). Required of a person's call relayed to a node, which may read
     * only a checkpoint of that conversation, in its repository.
     */
    conversationId: z.string().min(1).max(200).optional(),
    maxBytes: z.number().int().min(1024).max(GIT_DIFF_MAX_BYTES).default(GIT_DIFF_DEFAULT_MAX_BYTES),
    maxFiles: z.number().int().min(1).max(GIT_DIFF_MAX_FILES).default(GIT_DIFF_DEFAULT_MAX_FILES),
})
    .refine((v) => !!v.task !== !!v.checkpoint, { message: "name a task or a checkpoint, not both" });
export const diffLineSchema = z.object({
    kind: z.enum([" ", "+", "-"]),
    text: z.string(),
    /** The line's number in the old file (absent for an added line). */
    oldNo: z.number().int().positive().optional(),
    /** The line's number in the new file (absent for a removed line). */
    newNo: z.number().int().positive().optional(),
});
export const diffHunkSchema = z.object({
    oldStart: z.number().int().nonnegative(),
    oldLines: z.number().int().nonnegative(),
    newStart: z.number().int().nonnegative(),
    newLines: z.number().int().nonnegative(),
    lines: z.array(diffLineSchema),
});
export const DIFF_FILE_STATUSES = ["added", "modified", "deleted", "renamed", "copied", "type-changed"];
export const diffFileSchema = z.object({
    path: z.string(),
    /** A rename's old path. */
    oldPath: z.string().optional(),
    status: z.enum(DIFF_FILE_STATUSES),
    binary: z.boolean().optional(),
    insertions: z.number().int().nonnegative().optional(),
    deletions: z.number().int().nonnegative().optional(),
    hunks: z.array(diffHunkSchema),
    /** This file's hunks were cut: why, in words. */
    truncated: z.object({ reason: z.string().max(300) }).optional(),
});
export const gitDiffAnswerSchema = z.object({
    files: z.array(diffFileSchema),
    /** More changed than is here (a file cut, files left out, the byte cap). */
    truncated: z.boolean().optional(),
    /** What is missing and why, in a sentence. */
    note: z.string().max(400).optional(),
    /** What the diff is of, in words: "T-14: Add the login page, against where it started". */
    title: z.string().max(300).optional(),
});
// ── Review notes ───────────────────────────────────────────────────────
/**
 * A note on a line (D341). It addresses a line by file, side and line number in that side's file, never by position in a
 * diff, so it still means the same line when the diff is read again with other context or after the agent's next turn
 * (src/git/diffParse.ts `findNoteLine`).
 */
export const reviewNoteSchema = z
    .object({
    path: z.string().min(1).max(1000),
    /** `new`: the line number is in the file after the change; `old`: in the file before it (a removed line). */
    side: z.enum(["old", "new"]),
    line: z.number().int().positive(),
    endLine: z.number().int().positive().optional(),
    text: z.string().min(1).max(2000),
})
    .refine((n) => n.endLine === undefined || n.endLine >= n.line, { message: "endLine is before line" });
export const REVIEW_NOTES_MAX = 50;
export const reviewNotesSendSchema = z.object({
    rootId: z.string().min(1).max(200),
    sessionId: z.string().min(1).max(200),
    notes: z.array(reviewNoteSchema).min(1).max(REVIEW_NOTES_MAX),
});
/** How many distinct lines a batch of notes covers (a range counts each line once). */
export function reviewNoteLineCount(notes) {
    const seen = new Set();
    for (const n of notes)
        for (let l = n.line; l <= Math.min(n.endLine ?? n.line, n.line + 999); l++)
            seen.add(`${n.path}\0${n.side}\0${l}`);
    return seen.size;
}
/** One note's address in words: `src/a.ts:12`, `src/a.ts:12-15`, or `src/a.ts:12 (the line before the change)`. */
export function reviewNoteAddress(n) {
    const range = n.endLine && n.endLine !== n.line ? `${n.line}-${n.endLine}` : `${n.line}`;
    return `${n.path}:${range}${n.side === "old" ? " (before the change)" : ""}`;
}
/** The one prompt a batch of notes becomes: "Review notes on 3 lines:" and one entry per note, in words. */
export function renderReviewNotes(notes) {
    const n = reviewNoteLineCount(notes);
    const entries = notes.map((note, i) => `${i + 1}. ${reviewNoteAddress(note)} — ${note.text.trim().replace(/\r?\n/g, "\n   ")}`);
    return [`Review notes on ${n} line${n === 1 ? "" : "s"}:`, ...entries, "", "Please address each note, then say what you changed."].join("\n");
}
// ── Pairing ────────────────────────────────────────────────────────────
export const COMPANION_PAIRING_PREFIX = "mojoup-pair:";
export const COMPANION_PAIRING_VERSION = 1;
/** A pairing code lives at most this long. */
export const COMPANION_PAIRING_MAX_MS = 10 * 60_000;
export const COMPANION_CODE_LENGTH = 8;
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const CODE_RE = /^[0-9A-HJKMNP-TV-Z]{8}$/;
/** What a QR code (or the pasted line) carries to pair the phone. */
export const companionPairingSchema = z.object({
    v: z.literal(COMPANION_PAIRING_VERSION),
    /** `workbench`: the Control API's origin (`https://name.tailnet.ts.net:7421`). `hub`: the hub's API base (`https://…/hub/v1`). Always https. */
    endpoint: z.string().url(),
    /** One time, 8 characters of Crockford base32 (no I, L, O, U), typed in any case with or without a dash. */
    code: z.string().regex(CODE_RE),
    /** `sha256:<64 hex>` of the TLS certificate the endpoint serves: the phone refuses a connection to any other. */
    fingerprint: z.string().regex(/^sha256:[0-9a-f]{64}$/),
    /** ISO time the code stops working; never more than {@link COMPANION_PAIRING_MAX_MS} after it was made. */
    expiresAt: z.string().max(40),
    kind: z.enum(["workbench", "hub"]),
});
/** What `companion.pair.begin` takes. `terminals`: also grant {@link COMPANION_TERMINAL_SCOPES}. */
export const companionPairBeginSchema = z.object({ terminals: z.boolean().optional(), kind: z.enum(["workbench", "hub"]).optional() });
/** The canonical code (upper case, no dash or spaces, I/L as 1 and O as 0), or undefined. */
export function normalisePairingCode(input) {
    const s = String(input ?? "").toUpperCase().replace(/[\s-]/g, "").replace(/[IL]/g, "1").replace(/O/g, "0");
    return CODE_RE.test(s) ? s : undefined;
}
export function formatPairingCode(code) {
    return `${code.slice(0, 4)}-${code.slice(4)}`;
}
/** A code from 5 random bytes (40 bits, 8 characters). The caller supplies the randomness. */
export function pairingCodeFromBytes(bytes) {
    if (bytes.length < 5)
        throw new Error("a pairing code needs 5 random bytes");
    let bits = 0n;
    for (let i = 0; i < 5; i++)
        bits = (bits << 8n) | BigInt(bytes[i] ?? 0);
    let out = "";
    for (let i = COMPANION_CODE_LENGTH - 1; i >= 0; i--)
        out += CROCKFORD[Number((bits >> BigInt(i * 5)) & 31n)];
    return out;
}
function b64urlEncode(text) {
    let bin = "";
    for (const b of new TextEncoder().encode(text))
        bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlDecode(s) {
    if (!/^[A-Za-z0-9_-]+$/.test(s))
        return undefined;
    try {
        const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
        return new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
    }
    catch {
        return undefined;
    }
}
/**
 * `mojoup-pair:1.<w|h>.<b64url(endpoint)>.<fingerprint hex>.<CODE>.<expires, epoch seconds>` — one line, shaped like the
 * hub's `mojoup-hub:<b64url(apiUrl)>.<fp>.<CODE>` (shared/schemas/teamHub.ts), which a hub still accepts as it is.
 */
export function formatPairingUri(p) {
    const parsed = companionPairingSchema.parse(p);
    const expires = Math.floor(Date.parse(parsed.expiresAt) / 1000);
    if (!Number.isFinite(expires))
        throw new Error("expiresAt is not a time");
    return `${COMPANION_PAIRING_PREFIX}${parsed.v}.${parsed.kind === "hub" ? "h" : "w"}.${b64urlEncode(parsed.endpoint.replace(/\/+$/, ""))}.${parsed.fingerprint.slice(7)}.${parsed.code}.${expires}`;
}
/** A pairing line as scanned or pasted, or undefined when it is not one (an expired one still parses: the phone says so). */
export function parsePairingUri(input) {
    if (typeof input !== "string")
        return undefined;
    const s = input.trim().replace(/\s+/g, "");
    if (!s.toLowerCase().startsWith(COMPANION_PAIRING_PREFIX))
        return undefined;
    const parts = s.slice(COMPANION_PAIRING_PREFIX.length).split(".");
    if (parts.length !== 6)
        return undefined;
    // Destructured after the length check: the mirror compiles under `noUncheckedIndexedAccess` too.
    const [version, kindLetter, endpointPart, fingerprintPart, codePart, expiryPart] = parts;
    if (version !== String(COMPANION_PAIRING_VERSION) || !/^[wh]$/.test(kindLetter))
        return undefined;
    const endpoint = b64urlDecode(endpointPart);
    const code = normalisePairingCode(codePart);
    const seconds = Number(expiryPart);
    if (!endpoint || !code || !Number.isInteger(seconds) || seconds < 1_600_000_000 || !/^[0-9a-fA-F]{64}$/.test(fingerprintPart))
        return undefined;
    const out = companionPairingSchema.safeParse({ v: COMPANION_PAIRING_VERSION, endpoint, code, fingerprint: `sha256:${fingerprintPart.toLowerCase()}`, expiresAt: new Date(seconds * 1000).toISOString(), kind: kindLetter === "h" ? "hub" : "workbench" });
    return out.success && out.data.endpoint.startsWith("https://") ? out.data : undefined;
}
/** Whether a pairing is still good at `now` (ms), and never longer-lived than {@link COMPANION_PAIRING_MAX_MS} from `madeAt`. */
export function pairingIsLive(p, now) {
    const t = Date.parse(p.expiresAt);
    return Number.isFinite(t) && t > now;
}
//# sourceMappingURL=companion.js.map